import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyOffOutlinedIcon from "@mui/icons-material/KeyOffOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Dataset,
  DatasetLink,
  DatasetMember,
  DatasetRelease,
  DatasetReleaseMember,
  DatasetReleases,
  deleteDataset,
  getDataset,
  listDatasetReleases,
  releaseDataset,
  removeTableFromDataset,
  requestDatasetTableAccess,
  updateDataset,
  ReleaseValidationError,
  ReleaseValidationGates,
} from "@/lib/api/datasets";
import { useAuth } from "@/contexts/AuthProvider";
import { EditableName } from "@/components/canvas/utils/EditableName";
import { DatasetDescription } from "@/components/main/datasets/DatasetDescription";
import { DatasetTextMeta } from "@/components/main/datasets/DatasetTextMeta";
import {
  DatasetLicense,
  type DatasetLicenseValue,
} from "@/components/main/datasets/DatasetLicense";
import { DatasetLabels } from "@/components/main/datasets/DatasetLabels";
import { DatasetLinks } from "@/components/main/datasets/DatasetLinks";
import { MaintenanceGuideSection } from "@/components/main/datasets/MaintenanceGuideSection";
import { DatasetCreators } from "@/components/main/datasets/DatasetCreators";
import { DatasetKeywords } from "@/components/main/datasets/DatasetKeywords";
import {
  DatasetMetaSection,
  MetaSectionAccordion,
} from "@/components/main/datasets/DatasetMetaSection";
import { TableChips } from "@/components/main/datasets/TableChips";
import FormatQuoteOutlinedIcon from "@mui/icons-material/FormatQuoteOutlined";
import CopyrightOutlinedIcon from "@mui/icons-material/CopyrightOutlined";
import { ShareDataset } from "@/components/main/datasets/ShareDataset";
import {
  RELEASE_COMPOSER_HELPER,
  releaseErrorMessage,
} from "@/components/main/datasets/releaseCopy";
import { Button } from "@/components/common/Button";
import { Collapsible } from "@/components/common/Collapsible";
import { TooltipButton } from "@/components/common/TooltipButton";
import { NumberCount } from "@/components/common/NumberCount";
import { StatusChip } from "@/components/common/StatusChip";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { Tooltip } from "@/components/common/Tooltip";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import { DropdownMenuItem } from "@/components/common/DropdownMenuItem";
import {
  InlineComposer,
  InlineComposerButton,
} from "@/components/common/InlineComposerButton";
import { Inline, Stack } from "@/components/common/Stack";
import {
  ContextMenu,
  type ContextMenuItem,
} from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import { Spinner } from "@/components/common/Spinner";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { Dialog } from "@/components/main/utils/Dialog";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

const formatDate = (rfc3339: string): string =>
  new Date(rfc3339).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

const formatDateFull = (rfc3339: string): string =>
  new Date(rfc3339).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/// One conflict in the publish-validation dialog. Reuses the dataset subsection
/// header (`DatasetMetaSection`: icon + medium label) so a conflict reads like
/// the Description / Related links / Tables sections — icon tinted warning, the
/// fix hint and any affected tables rendered as the body.
const ValidationSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  hint: string;
  children?: React.ReactNode;
}> = ({ icon, title, hint, children }) => (
  <DatasetMetaSection
    icon={icon}
    label={title}
    labelClassName="text-fg-medium"
    headingPl="pl-0"
    bodyPl="pl-8"
    pr="pr-0"
  >
    <Stack gap="sm" className="min-w-0">
      <span className="text-fg-muted">{hint}</span>
      {children}
    </Stack>
  </DatasetMetaSection>
);

const ReleaseControl: React.FC<{
  releases: DatasetRelease[];
  selectedReleaseId: string | null;
  onSelect: (id: string | null) => void;
  isManager: boolean;
  canRelease: boolean;
  /// True for catalog datasets, where cutting a release also publishes it —
  /// so the action reads "Release & Publish" rather than plain "Release".
  publishesOnRelease: boolean;
  /// Members that block a release (connector tables that can't be snapshotted).
  /// When non-empty, the release affordance stays clickable but opens a dialog
  /// explaining the blockers instead of the composer.
  unreleasableMembers: { table_id: string; name: string }[];
  onSubmitRelease: (label: string) => Promise<void>;
}> = ({
  releases,
  selectedReleaseId,
  onSelect,
  isManager,
  canRelease,
  publishesOnRelease,
  unreleasableMembers,
  onSubmitRelease,
}) => {
  const releaseActionLabel = publishesOnRelease
    ? "Release & Publish"
    : "Release";
  const [open, setOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const addItemRef = useRef<HTMLDivElement>(null);

  // The composer is a sub-dropdown off the open menu; close it with the menu.
  useEffect(() => {
    if (!open) setComposerOpen(false);
  }, [open]);

  // A resolved submit (release cut, or a validation dialog opened) closes the
  // menu so it doesn't linger behind the dialog. A thrown inline error rejects
  // — the await rethrows, so the menu stays open to surface it.
  const submitAndClose = async (label: string) => {
    await onSubmitRelease(label);
    setOpen(false);
    setComposerOpen(false);
  };

  // A release is blocked (not merely "nothing changed") when live members can't
  // be snapshotted. The affordance stays clickable and opens a dialog that
  // explains why, mirroring the publish-validation dialog.
  const blocked = isManager && unreleasableMembers.length > 0;

  const blockedDialog = (
    <Dialog
      open={showBlocked}
      title="Resolve this before releasing"
      onClose={() => setShowBlocked(false)}
    >
      <Stack gap="xs" style={{ fontSize: "var(--font-size)" }}>
        <ValidationSection
          icon={<CloudOutlinedIcon className="icon-sm text-warning" />}
          title="Tables from an external connection"
          hint="These tables sync live from an external connection and can't be part of a release. Remove them to release the rest."
        >
          <TableChips tables={unreleasableMembers} />
        </ValidationSection>
      </Stack>
    </Dialog>
  );

  if (releases.length === 0) {
    if (blocked) {
      return (
        <>
          <button
            type="button"
            onClick={() => setShowBlocked(true)}
            className="inline-flex items-center gap-1 shrink-0 min-h-[var(--control-height-sm)] px-2 py-0.5 rounded-app border border-dashed border-accent text-accent cursor-pointer hover:bg-accent-15 transition-colors"
            style={{ fontSize: "var(--font-size-small)" }}
          >
            <RocketLaunchOutlinedIcon className="icon-xs" />
            Add a release
          </button>
          {blockedDialog}
        </>
      );
    }
    return isManager && canRelease ? (
      <span className="shrink-0">
        <InlineComposerButton
          label="Add a release"
          icon={<RocketLaunchOutlinedIcon className="icon-xs" />}
          placeholder="Release name — e.g. 2026-q3"
          actionLabel={releaseActionLabel}
          helper={RELEASE_COMPOSER_HELPER}
          placement="bottom-end"
          onSubmit={submitAndClose}
        />
      </span>
    ) : null;
  }

  const selected = releases.find((r) => r.id === selectedReleaseId) ?? null;
  // A release is actively filtering the Tables list (manager or viewer).
  const active = selected !== null;
  const choose = (id: string) => {
    onSelect(isManager && id === selectedReleaseId ? null : id);
    setOpen(false);
  };
  // Managers return to the current dataset tables via the ✕; viewers are
  // release-locked and never see it.
  const clearRelease = () => {
    onSelect(null);
    setOpen(false);
  };

  return (
    <>
      <span
        ref={anchorRef}
        className={`inline-flex items-center shrink-0 min-h-(--control-height-sm) rounded-app border transition-colors ${
          active
            ? "border-accent text-accent bg-accent-15"
            : "border-border text-fg-muted hover:border-accent hover:text-accent"
        }`}
        style={{ fontSize: "var(--font-size-small)" }}
      >
        <button
          type="button"
          aria-label="Releases"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1 py-0.5 pl-2 cursor-pointer ${
            isManager && active ? "pr-1" : "pr-2"
          }`}
        >
          <RocketLaunchOutlinedIcon className="icon-xs" />
          <span className="truncate max-w-40">
            {selected ? selected.label : "Releases"}
          </span>
          {!selected && <NumberCount count={releases.length} size="sm" />}

          {!(isManager && active) && (
            <KeyboardArrowDownIcon className="icon-xs" />
          )}
        </button>
        {isManager && active && (
          <Tooltip title="Clear release" placement="top">
            <button
              type="button"
              aria-label="Clear release filter"
              onClick={clearRelease}
              className="inline-flex items-center py-0.5 pl-0.5 pr-2 cursor-pointer opacity-70 hover:opacity-100"
            >
              <CloseRoundedIcon className="icon-xs" />
            </button>
          </Tooltip>
        )}
      </span>
      <DropdownMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        placement="bottom-end"
        className="min-w-60"
        shouldIgnoreOutsideEvent={(e) =>
          e.target instanceof Element &&
          !!e.target.closest(".release-composer-popover")
        }
      >
        {releases.map((r, i) => (
          <DropdownMenuItem
            key={r.id}
            isSelected={r.id === selectedReleaseId}
            onClick={() => choose(r.id)}
          >
            <span
              className="truncate"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {r.label}
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {i === 0 && <StatusChip tone="accent">latest</StatusChip>}
              <Tooltip
                title={`Released ${formatDateFull(r.released_at)}`}
                placement="top"
              >
                <span
                  className="inline-flex items-center gap-1 font-mono text-fg-muted"
                  style={{ fontSize: "var(--font-size-x-small)" }}
                >
                  <AccessTimeIcon className="icon-xs opacity-70 shrink-0" />
                  {formatDate(r.released_at)}
                </span>
              </Tooltip>
            </span>
          </DropdownMenuItem>
        ))}
        {isManager && canRelease && (
          <div ref={addItemRef}>
            <div className="my-1 border-t border-border" />
            <DropdownMenuItem onClick={() => setComposerOpen((c) => !c)}>
              <span
                className="inline-flex items-center gap-1 text-accent"
                style={{ fontSize: "var(--font-size-small)" }}
              >
                <AddOutlinedIcon className="icon-xs" />
                Add release
              </span>
            </DropdownMenuItem>
          </div>
        )}
        {blocked && (
          <>
            <div className="my-1 border-t border-border" />
            <DropdownMenuItem
              onClick={() => {
                setOpen(false);
                setShowBlocked(true);
              }}
            >
              <span
                className="inline-flex items-center gap-1 text-accent"
                style={{ fontSize: "var(--font-size-small)" }}
              >
                <AddOutlinedIcon className="icon-xs" />
                Add release
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenu>
      {blockedDialog}
      <InlineComposer
        anchorRef={addItemRef}
        open={open && composerOpen}
        onClose={() => setComposerOpen(false)}
        placeholder="Release name — e.g. 2026-q3"
        actionLabel={releaseActionLabel}
        helper={RELEASE_COMPOSER_HELPER}
        placement="bottom"
        className="release-composer-popover"
        onSubmit={submitAndClose}
      />
    </>
  );
};

/// A tag marker next to a Tables member comparing the table's CURRENT version
/// to what the viewed release pinned: "ahead" (green — the table has changed
/// since this release), "current" (accent — unchanged since), or "behind" (red
/// — this release pins a newer version than the table has now; rare). `sync` is
/// null only when the current version is unknown (a shared viewer) — then a
/// releasable member shows nothing and an imported one just shows its source
/// release name.
const ReleaseDiff: React.FC<{
  member: DatasetReleaseMember;
  sync: "current" | "behind" | "ahead" | null;
}> = ({ member, sync }) => {
  const external = member.kind === "external";
  if (!external && sync === null) return null;
  const color =
    sync === "behind"
      ? "text-error"
      : sync === "ahead"
        ? "text-success"
        : sync === "current"
          ? "text-accent"
          : "text-fg-muted";
  const title = external
    ? `Imported — pinned to the source release "${member.release_name ?? "?"}"`
    : sync === "ahead"
      ? "Ahead of this release — the table has changed since"
      : sync === "behind"
        ? "Behind this release"
        : "Unchanged since this release";
  return (
    <Tooltip title={title} placement="top">
      <span
        className={`shrink-0 inline-flex items-center gap-1 ${color}`}
        style={{ fontSize: "var(--font-size-x-small)" }}
        data-testid={`release-diff${sync ? `-${sync}` : ""}`}
      >
        {external ? (
          <LayersOutlinedIcon className="icon-xs" />
        ) : (
          <RocketLaunchOutlinedIcon className="icon-xs" />
        )}
        {external && (
          <span className="truncate max-w-[9rem]">
            {member.release_name ?? "imported"}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

/// One row in the Tables list. Sourced from live membership (no release
/// selected) or from a selected release's immutable pin snapshot. `removed` is
/// true for a release member no longer in the dataset — still shown, but tagged
/// so it reads as historical. `name === null` ⇒ obfuscated (restricted).
interface TableDisplayRow {
  table_id: string;
  name: string | null;
  discoverable: boolean;
  parent_model_id: string | null;
  parent_model_name: string | null;
  /// Live membership add-date; null for a removed-in-release member.
  added_at: string | null;
  /// The selected release's pin for this table, if a release is selected.
  pin: DatasetReleaseMember | null;
  removed: boolean;
  /// The table is in its model's trash — still a member, still readable, but on
  /// its way out. Distinct from `hardDeleted` (already gone) and from
  /// `!discoverable` (exists, out of reach).
  scheduledForDeletion: boolean;
  hardDeleted: boolean;
}

interface DatasetRowProps {
  dataset: Dataset;
  onDeleted: (id: string) => void;
  onUpdated?: (id: string, description: string | null) => void;
  onRenamed?: (id: string, name: string) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /// Bumped when the shared organisation registry changes, so an open creator
  /// picker re-reads it without a page reload.
  registryVersion?: number;
}

/// The ask on an "unavailable" member. The caller cannot discover this table —
/// that is what "unavailable" means — so the standing comes from the referral:
/// the dataset id goes to the server, which verifies that this dataset lists
/// this table and that the caller may view the dataset.
const RequestMemberAccess: React.FC<{
  datasetId: string;
  tableId: string;
}> = ({ datasetId, tableId }) => {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );

  if (state === "sent") {
    return (
      <Tooltip
        title="The people who can grant it have been asked"
        placement="top"
      >
        <StatusChip tone="accent">requested</StatusChip>
      </Tooltip>
    );
  }

  return (
    <TooltipButton
      title={state === "error" ? "Couldn't send — try again" : "Request access"}
      variant="contextual"
      size="xs"
      disabled={state === "sending"}
      onClick={async (e) => {
        e.stopPropagation();
        setState("sending");
        const status = await requestDatasetTableAccess(datasetId, tableId);
        // `already_requested` and `already_readable` are both successful
        // outcomes for the person clicking: somebody has been asked, or there
        // was nothing to ask for.
        setState(status === "error" ? "error" : "sent");
      }}
    >
      <LockOpenOutlinedIcon />
    </TooltipButton>
  );
};

export const DatasetRow: React.FC<DatasetRowProps> = ({
  dataset,
  onDeleted,
  onUpdated,
  onRenamed,
  expanded,
  onExpandedChange,
  registryVersion = 0,
}) => {
  const navigate = useNavigate();
  const { canPublishDataset, canManagePublicCatalog } = useAuth();
  const menu = useContextMenu();
  const [members, setMembers] = useState<DatasetMember[] | null>(null);
  const [links, setLinks] = useState<DatasetLink[]>([]);
  const [releases, setReleases] = useState<DatasetRelease[]>([]);
  const [canRelease, setCanRelease] = useState(false);
  const [unreleasable, setUnreleasable] = useState<string[]>([]);
  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(
    null,
  );
  const [currentVersions, setCurrentVersions] = useState<
    Record<string, number>
  >({});
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [name, setName] = useState(dataset.name);
  const [isEditingName, setIsEditingName] = useState(false);
  const [description, setDescription] = useState(dataset.description);
  const [license, setLicense] = useState<DatasetLicenseValue>({
    license: dataset.license,
    license_id: dataset.license_id,
    license_name: dataset.license_name,
    license_url: dataset.license_url,
  });
  const [attribution, setAttribution] = useState(dataset.attribution);
  const [labels, setLabels] = useState(dataset.labels);
  const [keywords, setKeywords] = useState(dataset.keywords);
  const [creators, setCreators] = useState(dataset.creators);
  const [isPublic, setIsPublic] = useState(dataset.is_public);
  const [validation, setValidation] = useState<ReleaseValidationGates | null>(
    null,
  );
  const [removingId, setRemovingId] = useState<string | null>(null);

  const canManage = dataset.is_catalog
    ? canManagePublicCatalog
    : dataset.is_owner;
  const showPublicMeta = isPublic || dataset.is_catalog;
  const canReleaseHere =
    canManage && (!dataset.is_catalog || canPublishDataset);

  const loadMembers = useCallback(async () => {
    if (members !== null || loading) return;
    setLoading(true);
    try {
      const [detail, rels] = await Promise.all([
        getDataset(dataset.id),
        listDatasetReleases(dataset.id).catch(
          () =>
            ({
              releases: [],
              canRelease: false,
              currentVersions: {},
              unreleasable: [],
            }) as DatasetReleases,
        ),
      ]);
      setMembers(detail.members);
      setLinks(detail.links);
      setReleases(rels.releases);
      setCanRelease(rels.canRelease);
      setCurrentVersions(rels.currentVersions);
      setUnreleasable(rels.unreleasable ?? []);
    } catch {
      showNotificationGlobal("Failed to load dataset members", "error");
      onExpandedChange(false);
    } finally {
      setLoading(false);
    }
  }, [members, loading, dataset.id, onExpandedChange]);

  const submitRelease = useCallback(
    async (label: string) => {
      let rel: DatasetRelease;
      try {
        rel = await releaseDataset(dataset.id, label);
      } catch (e) {
        if (e instanceof ReleaseValidationError) {
          setValidation(e.gates);
          return;
        }
        throw new Error(releaseErrorMessage(e));
      }
      setReleases((prev) => [rel, ...prev]);
      setSelectedReleaseId(rel.id);
      setCanRelease(false);
      if (dataset.is_catalog) setIsPublic(true);
      showNotificationGlobal(`Released "${label}"`, "success");
    },
    [dataset.id, dataset.is_catalog],
  );

  useEffect(() => {
    if (expanded) void loadMembers();
  }, [expanded, loadMembers]);

  const toggle = useCallback(
    () => onExpandedChange(!expanded),
    [expanded, onExpandedChange],
  );

  const handleRemoveMember = useCallback(
    async (tableId: string) => {
      setRemovingId(tableId);
      try {
        await removeTableFromDataset(dataset.id, tableId);
        const remaining = (members ?? []).filter((m) => m.table_id !== tableId);
        setMembers(remaining);
        const remainingBlockers = unreleasable.filter((id) => id !== tableId);
        setUnreleasable(remainingBlockers);
        // Membership changed ⇒ a release is warranted again, but only if ≥1
        // member remains and none of them still blocks a snapshot.
        setCanRelease(remaining.length > 0 && remainingBlockers.length === 0);
      } catch {
        showNotificationGlobal("Failed to remove table from dataset", "error");
      } finally {
        setRemovingId(null);
      }
    },
    [dataset.id, members, unreleasable],
  );

  const onDescriptionSaved = useCallback(
    (next: string | null) => {
      setDescription(next);
      onUpdated?.(dataset.id, next);
    },
    [dataset.id, onUpdated],
  );

  // Rename via the shared `EditableName` (same component + style as model/team).
  // `commit` returns false to keep the input open (empty name) — the parent gate
  // (`canManage`) already matches the server: owner for owned, manager for
  // catalog. The write is optimistic and reverts on failure.
  const commitName = useCallback(
    (next: string): boolean => {
      const trimmed = next.trim();
      if (!trimmed) {
        showNotificationGlobal("Dataset name cannot be empty", "error");
        return false;
      }
      if (trimmed === name) {
        setIsEditingName(false);
        return true;
      }
      const prev = name;
      setName(trimmed);
      setIsEditingName(false);
      updateDataset(dataset.id, { name: trimmed })
        .then((updated) => onRenamed?.(dataset.id, updated.name))
        .catch(() => {
          setName(prev);
          showNotificationGlobal("Failed to rename dataset", "error");
        });
      return true;
    },
    [name, dataset.id, onRenamed],
  );

  const onConfirmDelete = useCallback(async () => {
    setConfirmDelete(false);
    try {
      await deleteDataset(dataset.id);
      showNotificationGlobal(`Deleted "${dataset.name}"`, "success");
      onDeleted(dataset.id);
    } catch {
      showNotificationGlobal("Failed to delete dataset", "error");
    }
  }, [dataset.id, dataset.name, onDeleted]);

  const roleLabel = dataset.is_owner ? "Owner" : "Shared";

  useEffect(() => {
    setSelectedReleaseId((cur) => {
      if (cur && releases.some((r) => r.id === cur)) return cur;
      return canManage ? null : (releases[0]?.id ?? null);
    });
  }, [releases, canManage]);
  const selectedRelease =
    releases.find((r) => r.id === selectedReleaseId) ??
    (canManage ? null : (releases[0] ?? null));

  // The Tables list. With no release selected it's the live membership. With a
  // release selected it's that release's IMMUTABLE pin snapshot — every table
  // that was part of it, joined to live membership for display metadata where
  // the table is still a member, else shown from the release-time name snapshot
  // and tagged "removed". This keeps a release a faithful record of what it
  // contained, independent of the current membership set.
  const shownRows: TableDisplayRow[] | null =
    members === null
      ? null
      : selectedRelease
        ? (() => {
            const byId = new Map(members.map((m) => [m.table_id, m]));
            return selectedRelease.members.map((rm) => {
              const live = byId.get(rm.table_id);
              return live
                ? {
                    table_id: live.table_id,
                    name: live.name,
                    discoverable: live.discoverable,
                    parent_model_id: live.parent_model_id,
                    parent_model_name: live.parent_model_name,
                    added_at: live.added_at,
                    pin: rm,
                    removed: false,
                    scheduledForDeletion: live.scheduled_for_deletion,
                    hardDeleted: live.hard_deleted,
                  }
                : {
                    table_id: rm.table_id,
                    name: rm.name_at_release,
                    discoverable: false,
                    parent_model_id: null,
                    parent_model_name: null,
                    added_at: null,
                    pin: rm,
                    removed: true,
                    scheduledForDeletion: false,
                    hardDeleted: false,
                  };
            });
          })()
        : members.map((m) => ({
            table_id: m.table_id,
            name: m.name,
            discoverable: m.discoverable,
            parent_model_id: m.parent_model_id,
            parent_model_name: m.parent_model_name,
            added_at: m.added_at,
            pin: null,
            removed: false,
            scheduledForDeletion: m.scheduled_for_deletion,
            hardDeleted: m.hard_deleted,
          }));
  const shownCount = shownRows?.length ?? null;

  // CSS var (not a JS-resolved value) so the icon tint tracks light/dark and
  // matches the dataset's node color in graph mode.
  const datasetColorVar = `var(--token-color-cat-${dataset.color}, var(--token-color-cat-default))`;

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: "Rename",
      icon: <EditOutlinedIcon className="icon-md" />,
      onClick: () => setIsEditingName(true),
      disabled: !canManage,
    },
    {
      label: "Manage access",
      icon: <PersonAddOutlinedIcon className="icon-md" />,
      onClick: () => setIsShareOpen(true),
      disabled: !canManage || dataset.is_catalog,
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Delete",
      icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
      onClick: () => setConfirmDelete(true),
      disabled: !canManage || (isPublic && !canPublishDataset),
      hint:
        isPublic && !canPublishDataset
          ? "admin only, public dataset"
          : undefined,
      danger: true,
    },
  ];

  return (
    <div className="rounded-app group/card">
      {/* Expanded, the body outruns the list's scroll box — the header sticks so
          you can still see which dataset you're reading. It parks below the
          section label (`item-height` + its `mb-2`), which stays on top at z-20,
          and needs its own opaque background once content slides under it. */}
      <div
        className={`group flex items-center gap-2 px-1 py-1.5 rounded-app select-none cursor-default group-hover/card:bg-bg-hover transition-colors ${
          menu.isOpen ? "bg-bg-hover" : expanded ? "bg-bg" : ""
        } ${expanded ? "sticky z-10" : ""}`}
        style={
          expanded
            ? { top: "calc(var(--item-height) + var(--spacing-sm))" }
            : undefined
        }
        tabIndex={0}
        {...menu.triggerProps}
      >
        <Button
          variant="default"
          size="xs"
          aria-label={
            expanded ? `Collapse ${dataset.name}` : `Expand ${dataset.name}`
          }
          onClick={toggle}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </Button>
        <span
          className="flex items-center justify-center size-6 rounded-app shrink-0"
          style={{
            color: datasetColorVar,
            backgroundColor: `color-mix(in srgb, ${datasetColorVar} 20%, transparent)`,
          }}
        >
          <LayersOutlinedIcon className="icon-sm" />
        </span>
        <div className="min-w-0 flex-1">
          <Inline gap="xs" className="min-w-0 items-center">
            <span
              className="text-fg flex items-center min-w-0 shrink"
              style={{
                fontSize: "var(--font-size)",
                fontWeight: "var(--font-weight-medium)",
              }}
            >
              <EditableName
                name={name}
                isEditing={isEditingName}
                canEdit={canManage}
                editTrigger="icon"
                editColor={datasetColorVar}
                setIsEditing={setIsEditingName}
                update={() => {}}
                commit={commitName}
                abort={() => setIsEditingName(false)}
              />
            </span>
            {dataset.is_catalog ? (
              // Catalog datasets read differently for managers vs end users.
              // Managers see the publish workflow: "Catalog" (not yet published)
              // → "Published" (live). End users only ever encounter published
              // ones and see the consumer-facing "Public" — never "Catalog".
              isPublic ? (
                canManagePublicCatalog ? (
                  <StatusChip tone="accent">Published</StatusChip>
                ) : (
                  <StatusChip tone="accent">Public</StatusChip>
                )
              ) : (
                canManagePublicCatalog && (
                  <StatusChip tone="warning">Catalog</StatusChip>
                )
              )
            ) : (
              <>
                <span
                  className="text-small text-fg-muted shrink-0"
                  style={{ fontWeight: "var(--font-weight-regular)" }}
                >
                  {roleLabel}
                </span>
                {isPublic && <StatusChip tone="accent">Public</StatusChip>}
              </>
            )}
          </Inline>
        </div>
        {canManage && (
          <Inline
            gap="sm"
            className={`shrink-0 transition-opacity ${
              menu.isOpen
                ? ""
                : "opacity-0 group-hover/card:opacity-100 focus-within:opacity-100"
            }`}
          >
            {!dataset.is_catalog && (
              <TooltipButton
                title="Share"
                variant="contextual"
                size="xs"
                aria-label={`Share ${dataset.name}`}
                onClick={() => setIsShareOpen(true)}
              >
                <PersonAddOutlinedIcon />
              </TooltipButton>
            )}
          </Inline>
        )}
        {expanded && (
          <ReleaseControl
            releases={releases}
            selectedReleaseId={selectedReleaseId}
            onSelect={setSelectedReleaseId}
            isManager={canManage}
            canRelease={canReleaseHere && canRelease}
            publishesOnRelease={dataset.is_catalog}
            unreleasableMembers={
              members
                ? unreleasable.flatMap((id) => {
                    const name = members.find((m) => m.table_id === id)?.name;
                    return name ? [{ table_id: id, name }] : [];
                  })
                : []
            }
            onSubmitRelease={submitRelease}
          />
        )}
      </div>

      <Collapsible open={expanded} className="pb-2 cursor-default">
        {/* pr-0 aligns the "+ Add …" pills with the page's flush search
                bar / New dataset footer (left indent stays as a subsection). */}
        <MetaSectionAccordion defaultOpenLabel="Description">
          <DatasetDescription
            datasetId={dataset.id}
            description={description}
            canEdit={canManage}
            onSaved={onDescriptionSaved}
            pr="pr-0"
          />
          {showPublicMeta && (
            <>
              <DatasetLabels
                datasetId={dataset.id}
                labels={labels}
                canEdit={canManage}
                canManageCatalog={canManagePublicCatalog}
                onLabelsChange={setLabels}
                pr="pr-0"
              />
              <DatasetLicense
                datasetId={dataset.id}
                value={license}
                canEdit={canManage}
                onSaved={setLicense}
                pr="pr-0"
              />
              <DatasetTextMeta
                datasetId={dataset.id}
                field="attribution"
                value={attribution}
                label="Attribution"
                icon={<FormatQuoteOutlinedIcon className="icon-sm" />}
                addLabel="Add attribution"
                placeholder="e.g. Data © Natural Earth"
                canEdit={canManage}
                onSaved={setAttribution}
                pr="pr-0"
              />
              <DatasetCreators
                datasetId={dataset.id}
                creators={creators}
                canEdit={canManage}
                onChange={setCreators}
                registryVersion={registryVersion}
                pr="pr-0"
              />
              <DatasetKeywords
                datasetId={dataset.id}
                keywords={keywords}
                canEdit={canManage}
                onChange={setKeywords}
                pr="pr-0"
              />
            </>
          )}
          {members !== null && (
            <DatasetLinks
              datasetId={dataset.id}
              links={links}
              canEdit={canManage}
              onLinksChange={setLinks}
              pr="pr-0"
            />
          )}
          <MaintenanceGuideSection
            datasetId={dataset.id}
            datasetName={dataset.name}
            canManage={canManage}
            expanded={expanded}
            pr="pr-0"
          />
        </MetaSectionAccordion>
        <DatasetMetaSection
          icon={<TableChartOutlinedIcon className="icon-sm" />}
          label="Tables"
          pr="pr-0"
          meta={
            shownCount !== null ? (
              <NumberCount count={shownCount} size="sm" />
            ) : undefined
          }
        />
        {loading ? (
          <div className="flex items-center gap-2 pl-16 py-2 text-small text-fg-muted">
            <Spinner className="icon-sm" /> Loading members…
          </div>
        ) : shownRows && shownRows.length === 0 ? (
          <div className="pl-16 py-2 text-small text-fg-muted italic">
            {selectedRelease
              ? `No tables in "${selectedRelease.label}".`
              : "No tables yet."}
          </div>
        ) : (
          // Cap at 4 rows (item-height ×4 + the 3 inter-row gaps), then scroll.
          <div
            className="overflow-y-auto"
            style={{
              maxHeight:
                "calc(var(--item-height) * 4 + var(--spacing-2xs) * 3)",
            }}
          >
            <Stack gap="2xs">
              {shownRows?.map((row) => {
                const current = row.pin
                  ? currentVersions[row.table_id]
                  : undefined;
                const sync =
                  row.pin && current !== undefined
                    ? current > row.pin.version
                      ? "ahead"
                      : current < row.pin.version
                        ? "behind"
                        : "current"
                    : null;
                const pin = row.pin ? (
                  <ReleaseDiff member={row.pin} sync={sync} />
                ) : null;
                return row.name === null ? (
                  <div
                    key={row.table_id}
                    className="flex items-center gap-3 ml-14 px-2 rounded-app item-height select-none"
                  >
                    <LockOutlinedIcon className="icon-xs text-fg-muted shrink-0" />
                    <span
                      className="italic text-fg-muted truncate"
                      style={{ fontSize: "var(--font-size-small)" }}
                    >
                      Restricted table
                    </span>
                    {pin}
                    {row.added_at && (
                      <Tooltip
                        title={`Added ${formatDateFull(row.added_at)}`}
                        placement="top"
                      >
                        <span
                          className="inline-flex items-center gap-1 font-mono text-fg-muted ml-auto shrink-0"
                          style={{ fontSize: "var(--font-size-x-small)" }}
                        >
                          <AccessTimeIcon className="icon-xs opacity-70 shrink-0" />
                          {formatDate(row.added_at)}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <div
                    key={row.table_id}
                    className="group flex items-center gap-3 ml-14 px-2 rounded-app item-height select-none hover:bg-bg-hover transition-colors"
                  >
                    <span
                      className={`truncate min-w-0 ${row.discoverable ? "text-fg" : "text-fg-muted"}`}
                      style={{ fontSize: "var(--font-size)" }}
                    >
                      {row.name}
                    </span>
                    {pin}
                    {row.parent_model_id && row.parent_model_name && (
                      <Tooltip
                        title={`Open in ${row.parent_model_name}`}
                        placement="top"
                      >
                        <Button
                          variant="quiet-link"
                          size="xs"
                          className="truncate shrink-0 max-w-[45%]"
                          onClick={() =>
                            navigate(
                              `/canvas/${row.parent_model_id}?table=${encodeURIComponent(row.table_id)}`,
                            )
                          }
                        >
                          {row.parent_model_name}
                          <ArrowOutwardIcon className="icon-xs shrink-0" />
                        </Button>
                      </Tooltip>
                    )}
                    {row.removed ? (
                      <Tooltip
                        title="No longer a member of this dataset"
                        placement="top"
                      >
                        <StatusChip>removed</StatusChip>
                      </Tooltip>
                    ) : row.hardDeleted ? (
                      <Tooltip
                        title="This table was permanently deleted. It stays listed so the dataset's history reads true."
                        placement="top"
                      >
                        <StatusChip>deleted</StatusChip>
                      </Tooltip>
                    ) : row.scheduledForDeletion ? (
                      <Tooltip
                        title="This table is in its model's trash. Restore it there, or it will be permanently deleted."
                        placement="top"
                      >
                        <StatusChip>scheduled for deletion</StatusChip>
                      </Tooltip>
                    ) : (
                      !row.discoverable && (
                        <>
                          <Tooltip
                            title="You don't have access to this table"
                            placement="top"
                          >
                            <StatusChip>unavailable</StatusChip>
                          </Tooltip>
                          <RequestMemberAccess
                            datasetId={dataset.id}
                            tableId={row.table_id}
                          />
                        </>
                      )
                    )}

                    {!row.removed && row.added_at && (
                      <span className="ml-auto shrink-0 flex items-center justify-end gap-2">
                        {removingId === row.table_id ? (
                          <Spinner className="icon-sm" />
                        ) : (
                          <>
                            <Tooltip
                              title={`Added ${formatDateFull(row.added_at)}`}
                              placement="top"
                            >
                              <span
                                className="inline-flex items-center gap-1 font-mono text-fg-muted shrink-0"
                                style={{
                                  fontSize: "var(--font-size-x-small)",
                                }}
                              >
                                <AccessTimeIcon className="icon-xs opacity-70 shrink-0" />
                                {formatDate(row.added_at)}
                              </span>
                            </Tooltip>
                            {/* Releases are immutable — the remove
                                    affordance only exists in the current-tables
                                    view (no release selected). */}
                            {canManage && !selectedRelease && (
                              <span className="hidden group-hover:inline-flex group-focus-within:inline-flex items-center shrink-0">
                                <TooltipButton
                                  title="Remove from dataset"
                                  variant="contextual"
                                  size="xs"
                                  aria-label={`Remove ${row.name} from ${dataset.name}`}
                                  disabled={removingId !== null}
                                  onClick={() =>
                                    void handleRemoveMember(row.table_id)
                                  }
                                >
                                  <CloseRoundedIcon />
                                </TooltipButton>
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </Stack>
          </div>
        )}
      </Collapsible>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${dataset.name}`}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete dataset"
        message={
          <Stack gap="xs">
            <p>
              Permanently delete{" "}
              <span className="text-fg-bold">{dataset.name}</span>?
            </p>
            <p className="text-small text-fg-muted">
              This removes the dataset and its shares. The member tables
              themselves are not affected.
            </p>
          </Stack>
        }
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={() => void onConfirmDelete()}
        onCancel={() => setConfirmDelete(false)}
      />

      {isShareOpen && (
        <ShareDataset
          datasetId={dataset.id}
          datasetName={dataset.name}
          onClose={() => setIsShareOpen(false)}
        />
      )}

      {validation && (
        <Dialog
          open
          title="Resolve these before publishing"
          onClose={() => setValidation(null)}
        >
          <Stack gap="xs" style={{ fontSize: "var(--font-size)" }}>
            {validation.dataset_description && (
              <ValidationSection
                icon={<NotesOutlinedIcon className="icon-sm text-warning" />}
                title="Missing dataset description"
                hint="Add a description so people know what this dataset contains."
              />
            )}
            {validation.dataset_license && (
              <ValidationSection
                icon={
                  <CopyrightOutlinedIcon className="icon-sm text-warning" />
                }
                title="Missing licence"
                hint="Set a licence so people know what they may do with this data."
              />
            )}
            {validation.dataset_attribution && (
              <ValidationSection
                icon={
                  <FormatQuoteOutlinedIcon className="icon-sm text-warning" />
                }
                title="Missing attribution"
                hint="Credit the source this data came from."
              />
            )}
            {validation.missing_tables.length > 0 && (
              <ValidationSection
                icon={
                  <DeleteOutlineOutlinedIcon className="icon-sm text-warning" />
                }
                title="Deleted tables"
                hint="These tables no longer exist — remove them from the dataset."
              >
                <TableChips tables={validation.missing_tables} />
              </ValidationSection>
            )}
            {validation.missing_descriptions.length > 0 && (
              <ValidationSection
                icon={
                  <DescriptionOutlinedIcon className="icon-sm text-warning" />
                }
                title="Missing table descriptions"
                hint="Every published table needs its own description."
              >
                <TableChips tables={validation.missing_descriptions} />
              </ValidationSection>
            )}
            {validation.invalid_primary_keys.length > 0 && (
              <ValidationSection
                icon={<KeyOffOutlinedIcon className="icon-sm text-warning" />}
                title="Invalid primary key"
                hint="Remove duplicate or empty key values before publishing."
              >
                {validation.invalid_primary_keys.map((m) => (
                  <span key={m.table_id} className="text-fg truncate">
                    {m.name}
                    <span className="text-fg-muted">
                      {" — "}
                      {m.duplicate_groups > 0 &&
                        `${m.duplicate_groups} duplicate ${m.duplicate_groups === 1 ? "value" : "values"}`}
                      {m.duplicate_groups > 0 && m.null_key_rows > 0 && ", "}
                      {m.null_key_rows > 0 &&
                        `${m.null_key_rows} empty ${m.null_key_rows === 1 ? "value" : "values"}`}
                    </span>
                  </span>
                ))}
              </ValidationSection>
            )}
          </Stack>
        </Dialog>
      )}
    </div>
  );
};
